import React, { CSSProperties, useEffect, useState } from 'react';

import { ChevronLeft, ChevronRight, Download, Info, MessageSquare, MessagesSquare, X } from 'lucide-react';

import { IConversationForAnalysisReport, IMSARMBTSRapportCollapse, IMSARMBTSRapportCollapsesConversation, IMSARMBTSReport, IMSARMBTSRubricParameter } from '../../../../types/services.interfaces/mbts-ux';
import { EChatSource } from '../../../../types/entities.enum';

import { ConsentLock } from '../ConsentLock';

type TRubricLevel = 'highly-skilled' | 'proficient' | 'gap' | 'priority-gap';

interface AnalysisReportProps {
    report: IMSARMBTSReport;
    conversations: IConversationForAnalysisReport[];
    isConsentLocked: boolean;
    openConsentDataGather: () => void;
};
export const AnalysisReport = (props: AnalysisReportProps) => {
    const { report, conversations, isConsentLocked, openConsentDataGather } = props;

    const getOverallScore = (): number => {
        // Take the avg of all percentage scores and convert to a 5-point scale
        const avgPct = report.rubric_parameters.reduce((acc, curr) => acc + curr.score, 0) / report.rubric_parameters.length;
        return Number(((avgPct / 100) * 5).toFixed(1));
    };

    const getProficiencyCategory = () => {
        const score = getOverallScore();

        if (score <= 1.9) return "Practicing Leader";
        if (score <= 2.9) return "Emerging Leader";
        if (score <= 3.9) return "Proficient Leader";
        return "Highly Skilled Leader";
    };

    const getRubricLevel = (score: number): { title: string; level: TRubricLevel; color: string; } => {
        if (score >= 75) return { title: 'Highly Skilled', level: 'highly-skilled', color: '#059669' };
        if (score >= 50) return { title: 'Proficient', level: 'proficient', color: '#4338CA' };
        if (score >= 25) return { title: 'Gap', level: 'gap', color: '#D48A09' };

        return { title: 'Priority Gap', level: 'priority-gap', color: '#9F1239' };
    };

    const getExecutiveNarrative = () => {
        const intro = `<p>Congratulations on completing this simulation! Here's a detailed breakdown of your interaction, your areas of strength and opportunities to improve.</p>`;

        // Extract existing paragraphs from report.executive_narrative
        const paragraphs = report.executive_narrative
            .match(/<p>.*?<\/p>/g) || [];

        // Add prefixes
        if (paragraphs[0]) paragraphs[0] = paragraphs[0].replace("<p>", "<p><strong>Proficiency:</strong> ");
        if (paragraphs[1]) paragraphs[1] = paragraphs[1].replace("<p>", "<p><strong>Examples:</strong> ");
        if (paragraphs[2]) paragraphs[2] = paragraphs[2].replace("<p>", "<p><strong>Use:</strong> ");

        return intro + paragraphs.slice(0, 3).join("");
    };

    return (
        <div className='mbts_analysis__report'>
            <div className="mbts_analysis__report__download_report">
                <button
                    className='btn btn--sm btn--radius-sm btn--primary'
                    onClick={() => window.print()}
                    disabled={isConsentLocked}
                >
                    <Download />
                    <span>Download Report</span>
                </button>
            </div>
            <div className="mbts_analysis__report__head">
                <div className="mbts_analysis__report__head__left">
                    <p className='mbts_analysis__report__head__report_title'>Leadership Simulation Results & Development Summary</p>
                    <p className='mbts_analysis__report__head__report_description'>A behavioral audit of leadership influence and cognitive resilience during the <strong>"{report.scenario_title}"</strong> simulation.</p>
                </div>
                <div className="mbts_analysis__report__head__right">
                    <p><strong>Role:</strong> {report.player_current_role} ({report.player_current_level})</p>
                    <p><strong>Next Milestone:</strong> {report.player_milestone_role} ({report.player_milestone_level})</p>
                    <p>MINDSHIELD PROPRIETARY DASHBOARD</p>
                </div>
            </div>
            <div className="mbts_analysis__report__executive_narrative">
                <div className='mbts_analysis__report__executive_narrative__left_border' />
                <p className='mbts_analysis__report__section_title'>Executive Summary</p>
                <div className='mbts_analysis__report__executive_narrative__content'>
                    <div className='mbts_analysis__report__executive_narrative__content__left'>
                        <div className="mbts_analysis__report__executive_narrative__content__current_profile">
                            Current Profile: {report.cognitive_profile}
                        </div>
                        <ExecutiveNarrativeContentDetails
                            score={getOverallScore()}
                            title={getProficiencyCategory()}
                            custClass='d-none-desktop'
                        />
                        <div
                            className='mbts_analysis__report__executive_narrative__content__text'
                            dangerouslySetInnerHTML={{ __html: getExecutiveNarrative() }}
                        />
                    </div>
                    <div className='mbts_analysis__report__executive_narrative__content__right'>
                        <ExecutiveNarrativeContentDetails
                            score={getOverallScore()}
                            title={getProficiencyCategory()}
                            custClass='d-none-mobile'
                        />
                        <div className='mbts_analysis__report__executive_narrative__content__use'>
                            <p>How to Use This Summary Report</p>
                            <p>Focus on the specific recommendations highlighted in your report. Practice the suggested alternatives in low-stakes situations before applying them in critical moments.</p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mbts_analysis__report__mastery_framework">
                <p className='mbts_analysis__report__section_title'>The Mastery Framework</p>
                <div className='mbts_analysis__report__mastery_framework__content'>
                    <ConsentLock isConsentLocked={isConsentLocked} openConsentDataGather={openConsentDataGather} />
                    <div className='mbts_analysis__report__mastery_framework__graph'>
                        <Graph parameters={report.rubric_parameters} />
                        <div className='mbts_analysis__report__mastery_framework__graph__legend'>
                            <div className='mbts_analysis__report__mastery_framework__graph__legend__item'>
                                <div className='mbts_analysis__report__mastery_framework__graph__legend__item__ico' />
                                <p>CURRENT PROFILE</p>
                            </div>
                            <div className='mbts_analysis__report__mastery_framework__graph__legend__item'>
                                <p className='mbts_analysis__report__mastery_framework__graph__legend__item__ico'>--</p>
                                <p>BENCHMARK ({report.player_milestone_level})</p>
                            </div>
                        </div>
                    </div>
                    <div className='mbts_analysis__report__mastery_framework__details'>
                        <div className='mbts_analysis__report__mastery_framework__details__item'>
                            <div className='mbts_analysis__report__mastery_framework__details__item__head'>
                                <div />
                                <p>COGNITIVE&nbsp;SKILLS</p>
                                <div />
                            </div>
                            <div className='mbts_analysis__report__mastery_framework__details__item__parameters'>
                                {report.rubric_parameters.map(parameter => (
                                    <div
                                        key={parameter.testing_skill.title}
                                        className={`mbts_analysis__report__mastery_framework__details__item__parameters__item mbts_analysis__report__mastery_framework__details__item__parameters__item--${getRubricLevel(parameter.score).level}`}
                                    >
                                        <div className='mbts_analysis__report__mastery_framework__details__item__parameters__item__title'>
                                            <p>{parameter.testing_skill.title}</p>
                                            <Tooltip content={parameter.testing_skill.tooltip_content ?? ''} />
                                        </div>
                                        <div className='mbts_analysis__report__mastery_framework__details__item__parameters__item__progress'>
                                            <div className='mbts_analysis__report__mastery_framework__details__item__parameters__item__progress__track'>
                                                <div className='mbts_analysis__report__mastery_framework__details__item__parameters__item__progress__track__left' />
                                                <div className='mbts_analysis__report__mastery_framework__details__item__parameters__item__progress__track__right' />
                                            </div>
                                            <div className='mbts_analysis__report__mastery_framework__details__item__parameters__item__progress__track_divider' />
                                            <div
                                                className='mbts_analysis__report__mastery_framework__details__item__parameters__item__progress__thumb'
                                                style={{ width: `${parameter.score}%` }}
                                            />
                                        </div>
                                        <p className='mbts_analysis__report__mastery_framework__details__item__parameters__item__score'>{parameter.score}%</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <p className='mbts_analysis__report__mastery_framework__details__descr'>
                            Shaded area represents the Benchmark threshold (4.0/5.0) for Director-level leadership.
                        </p>
                    </div>
                </div>
            </div>
            <div className="mbts_analysis__report__deep_dive">
                <p className='mbts_analysis__report__section_title'>Performance Deep-Dive: {report.scenario_title}</p>
                <div className="mbts_analysis__report__deep_dive__content">
                    <ConsentLock isConsentLocked={isConsentLocked} openConsentDataGather={openConsentDataGather} />
                    <table className='mbts_analysis__report__deep_dive__table d-none-mobile'>
                        <thead>
                            <tr>
                                <th><p>RUBRIC PARAMETER</p></th>
                                <th><p>LEVEL</p></th>
                                <th><p>RECOMMENDATIONS</p></th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.rubric_parameters.map(parameter => (
                                <tr
                                    key={parameter.testing_skill.title}
                                    style={{ '--rubric-color': getRubricLevel(parameter.score).color } as CSSProperties}
                                >
                                    <td>
                                        <div className='mbts_analysis__report__deep_dive__table__param_title'>
                                            <p>{parameter.testing_skill.title}</p>
                                            <Tooltip content={parameter.testing_skill.tooltip_content ?? ''} />
                                        </div>
                                        <div className="mbts_analysis__report__deep_dive__table__param_border" />
                                    </td>
                                    <td><p className='mbts_analysis__report__deep_dive__table__param_level'>{getRubricLevel(parameter.score).title}</p></td>
                                    <td><p className='mbts_analysis__report__deep_dive__table__param_descr'>{parameter.recommendation}</p></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className='mbts_analysis__report__deep_dive__cards d-none-desktop'>
                        {report.rubric_parameters.map(parameter => (
                            <div
                                key={parameter.testing_skill.title}
                                className='mbts_analysis__report__deep_dive__cards__card'
                                style={{ '--rubric-color': getRubricLevel(parameter.score).color } as CSSProperties}
                            >
                                <div className='mbts_analysis__report__deep_dive__cards__card__head'>
                                    <div className='mbts_analysis__report__deep_dive__cards__card__param_title'>
                                        <p>{parameter.testing_skill.title}</p>
                                        <Tooltip content={parameter.testing_skill.tooltip_content ?? ''} />
                                    </div>
                                    <p className='mbts_analysis__report__deep_dive__cards__card__param_level'>{getRubricLevel(parameter.score).title}</p>
                                </div>
                                <p className='mbts_analysis__report__deep_dive__cards__card__param_descr_subtitle'>Recommendations</p>
                                <p className='mbts_analysis__report__deep_dive__cards__card__param_descr'>{parameter.recommendation}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <BehavioralAudit
                conversations={conversations}
                current_role={report.player_current_role}
                rapport_collapses={report.rapport_collapses_conversations.flatMap(rcc => rcc.rapport_collapses)}
                rapport_collapses_conversations={report.rapport_collapses_conversations}
                isConsentLocked={isConsentLocked}
                openConsentDataGather={openConsentDataGather}
            />
        </div>
    );
};

interface GraphProps {
    parameters: IMSARMBTSRubricParameter[];
};
const Graph = (props: GraphProps) => {
    const { parameters } = props;

    const MAX_SCORE = 100;
    const AXIS_ORDER = parameters.map(p => p.testing_skill.short_title);
    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleRadians: number) => {
        return {
            x: centerX + radius * Math.cos(angleRadians),
            y: centerY + radius * Math.sin(angleRadians),
        };
    };

    const buildPolygonPoints = (oParameters: typeof parameters, maxRadius: number) => {
        const centerX = 50;
        const centerY = 50;
        const angleStep = (2 * Math.PI) / AXIS_ORDER.length;

        return AXIS_ORDER.map((axis, index) => {
            const param = oParameters.find(p => p.testing_skill.short_title === axis);
            const score = param ? param.score : 0;

            const radius = (score / MAX_SCORE) * maxRadius;
            const angle = angleStep * index - Math.PI / 2; // rotate so first axis is top

            const { x, y } = polarToCartesian(centerX, centerY, radius, angle);
            return `${x},${y}`;
        }).join(' ');
    };

    const currentProfilePoints = buildPolygonPoints(parameters, 32);
    const forceMultiplierPoints = buildPolygonPoints(parameters.map(p => ({ ...p, score: 4.5 })), 32);

    return (
        <svg viewBox="-25 -20 150 140" className="mbts_analysis__report__mastery_framework__graph__diagram">
            {/* Grid */}
            {[10, 20, 30, 40].map(r => <circle key={r} r={r} />)}

            {/* Force Multiplier (L5) */}
            <polygon points={forceMultiplierPoints} className="target" />

            {/* Current Profile */}
            <polygon points={currentProfilePoints} className="current" />

            {/* Axis labels */}
            {AXIS_ORDER.map((label, index) => {
                const angle = (2 * Math.PI / AXIS_ORDER.length) * index - Math.PI / 2;
                const { x, y } = polarToCartesian(50, 50, 45, angle);

                return <text key={label} x={x} y={y}>{label}</text>;
            })}
        </svg>
    );
};

interface TooltipProps {
    content: string;
};
const Tooltip = ({ content }: TooltipProps) => {
    return (
        <div className='mbts_analysis__report__tooltip'>
            <Info className='mbts_analysis__report__tooltip__btn' />
            <div className='mbts_analysis__report__tooltip__content' dangerouslySetInnerHTML={{ __html: content }} />
        </div>
    );
};

const ExecutiveNarrativeContentDetails = ({ score, title, custClass }: { score: number; title: string; custClass: string; }) => {
    return (
        <div className={`mbts_analysis__report__executive_narrative__content__details ${custClass}`}>
            <div className='mbts_analysis__report__executive_narrative__content__details__scores'>
                <p>{score}<span>/5.0</span></p>
                <p>{title}</p>
            </div>
            <div className='mbts_analysis__report__executive_narrative__content__details__profidx'>
                <p>PROFICIENCY INDEX</p>
                <Tooltip
                    content={`
                        <span>
                            Leadership Competency Proficiency Levels:
                            <br/>• Highly Skilled (4.0-5.0): Sophisticated leadership with nuanced application
                            <br/>• Proficient (3.0-3.9): Solid foundations with room for elevation
                            <br/>• Emerging (2.0-2.9): Emerging skills needing deliberate development
                            <br/>• Practicing (0-1.9): Early stages requiring focused practice
                        <span>
                    `}
                />
            </div>
        </div>
    );
};

interface BehavioralAudiProps {
    conversations: IConversationForAnalysisReport[];
    current_role: string;
    rapport_collapses: IMSARMBTSRapportCollapse[];
    rapport_collapses_conversations: IMSARMBTSRapportCollapsesConversation[];
    isConsentLocked: boolean;
    openConsentDataGather: () => void;
};
const BehavioralAudit = (props: BehavioralAudiProps) => {
    const { conversations, current_role, rapport_collapses, rapport_collapses_conversations, isConsentLocked, openConsentDataGather } = props;

    const [selectedCollapse, setSelectedCollapse] = useState<{ idx: number; collapse: IMSARMBTSRapportCollapse } | null>(null);
    const [selectedRPConversation, setSelectedRPConversation] = useState<IMSARMBTSRapportCollapsesConversation | null>(null);
    const [transcript, setTranscript] = useState<IConversationForAnalysisReport | null>(null);

    useEffect(() => {
        if (!rapport_collapses.length) return;

        setSelectedCollapse({ idx: 0, collapse: rapport_collapses[0] });
    }, [rapport_collapses]);

    useEffect(() => {
        if (!rapport_collapses_conversations.length || !selectedCollapse) return;

        const convo = rapport_collapses_conversations.find(c => c.conversation_id === selectedCollapse.collapse.chat.conversation_id);
        if (!convo) return;

        setSelectedRPConversation(convo);
    }, [rapport_collapses_conversations, selectedCollapse]);

    const getCollapseLeft = (sessionStart: string, sessionEnd: string, timestamp: string): number => {
        const start = new Date(sessionStart).getTime();
        const end = new Date(sessionEnd).getTime();
        const mid = new Date(timestamp).getTime();

        const totalSeconds = (end - start) / 1000;
        const elapsedSeconds = (mid - start) / 1000;
        return (elapsedSeconds / totalSeconds) * 100;
    };

    const prevCollapse = () => {
        if (!selectedCollapse) return;
        if (selectedCollapse.idx <= 0) return;

        const newIdx = selectedCollapse.idx - 1;
        const clps = rapport_collapses[newIdx];

        setSelectedCollapse({ idx: newIdx, collapse: clps });

        if (selectedRPConversation) {
            const convo = rapport_collapses_conversations.find(c => c.conversation_id === clps.chat.conversation_id);
            if (!convo) return;

            setSelectedRPConversation(convo);
        };
    };

    const nextCollapse = () => {
        if (!selectedCollapse) return;
        if (selectedCollapse.idx >= (rapport_collapses.length - 1)) return;

        const newIdx = selectedCollapse.idx + 1;
        const clps = rapport_collapses[newIdx];

        setSelectedCollapse({ idx: newIdx, collapse: clps });

        if (selectedRPConversation) {
            const convo = rapport_collapses_conversations.find(c => c.conversation_id === clps.chat.conversation_id);
            if (!convo) return;

            setSelectedRPConversation(convo);
        };
    };

    const getElapsedLabel = (sessionStart: string, timestamp: string) => {
        const start = new Date(sessionStart).getTime();
        const ts = new Date(timestamp).getTime();

        const elapsedSeconds = Math.floor((ts - start) / 1000);
        const minutes = Math.floor(elapsedSeconds / 60);
        const seconds = elapsedSeconds % 60;

        return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    const handleViewTranscript = () => {
        if (!selectedCollapse || !selectedRPConversation) return;

        const convo = conversations.find(c => c.id === selectedRPConversation.conversation_id);
        if (!convo) return;

        setTranscript(convo);
    };

    const getDistinctPersonas = () => {
        return rapport_collapses_conversations.map(c => c.conversation.persona);
    };

    const handleChangePersona = (personaId: string) => {
        const rpConvo = rapport_collapses_conversations.find(rpc => rpc.conversation.persona_id === personaId);
        if (!rpConvo) return;

        const rapportCollapseIdx = rapport_collapses.findIndex(rc => {
            const convo = conversations.find(c => c.id === rc.chat.conversation_id);
            if (convo) {
                return convo.persona_id === personaId;
            };
            return false;
        });
        if (rapportCollapseIdx === -1) return;

        setSelectedCollapse({ idx: rapportCollapseIdx, collapse: rapport_collapses[rapportCollapseIdx] });

        if (selectedRPConversation) {
            setSelectedRPConversation(rpConvo);
        };
    };

    useEffect(() => {
        if (!selectedCollapse || !selectedRPConversation || !transcript) return;

        requestAnimationFrame(() => {
            const el = document.getElementById(`chat_msg--${selectedCollapse.collapse.chat_id}`);
            if (!el) return;

            el.scrollIntoView({ behavior: 'smooth', block: 'end' });
        });
    }, [selectedRPConversation, selectedCollapse, transcript]);

    return (
        <div className="mbts_analysis__report__behavioral_audit">
            <p className='mbts_analysis__report__section_title'>SIMULATION LAB: BEHAVIORAL AUDIT</p>
            <div className="mbts_analysis__report__behavioral_audit__consent_lock_wrapper">
                <ConsentLock isConsentLocked={isConsentLocked} openConsentDataGather={openConsentDataGather} />
                {getDistinctPersonas().length > 1 && !!selectedRPConversation && (
                    <div className='mbts_analysis__report__behavioral_audit__personas'>
                        {getDistinctPersonas().map(persona => (
                            <button
                                key={persona.id}
                                className={`mbts_analysis__report__behavioral_audit__personas__persona_btn ${persona.id === selectedRPConversation.conversation.persona_id ? 'mbts_analysis__report__behavioral_audit__personas__persona_btn--active' : ''}`}
                                onClick={() => handleChangePersona(persona.id)}
                            >
                                {persona.title[0].toUpperCase() + persona.title.slice(1).replace('-', ' ')}
                            </button>
                        ))}
                    </div>
                )}
                {!!selectedCollapse && !!selectedRPConversation && (
                    <div className='mbts_analysis__report__behavioral_audit__content'>
                        <div className='mbts_analysis__report__behavioral_audit__content__left'>
                            <p className='mbts_analysis__report__behavioral_audit__content__title'>Interaction Heatmap</p>
                            <p className='mbts_analysis__report__behavioral_audit__content__descr'>Tracking the correlation between your dialogue choices and stakeholder rapport.</p>
                            <div className='mbts_analysis__report__behavioral_audit__content__rapport'>
                                <div className='mbts_analysis__report__behavioral_audit__content__rapport__legend'>
                                    <p>SESSION START</p>
                                    <p>
                                        FRICTION POINTS
                                        {/* {selectedRPConversation.status === 'successful' ? 'FRICTION' : selectedRPConversation.status === 'stalemate' ? 'COLLAPSE/FRICTION' : 'RAPPORT COLLAPSE'} */}
                                    </p>
                                    <p>SESSION END</p>
                                </div>
                                <div className={`mbts_analysis__report__behavioral_audit__content__rapport__bar mbts_analysis__report__behavioral_audit__content__rapport__bar--${selectedRPConversation.status}`}>
                                    {!!rapport_collapses.length &&
                                        <div
                                            className='mbts_analysis__report__behavioral_audit__content__rapport__bar__divider'
                                            style={{ left: `${getCollapseLeft(selectedRPConversation.session_start, selectedRPConversation.session_end, selectedCollapse.collapse.chat.created_at)}%` }}
                                        />
                                    }
                                </div>
                                <div className='mbts_analysis__report__behavioral_audit__content__rapport__analysis'>
                                    <p>At <strong>{getElapsedLabel(selectedRPConversation.session_start, selectedCollapse.collapse.chat.created_at)}</strong>, {selectedCollapse.collapse.analysis}</p>
                                </div>
                            </div>
                            <div className='mbts_analysis__report__behavioral_audit__content__session_highlight'>
                                <MessagesSquare />
                                <p>Session Highlight: {selectedRPConversation.highlight}</p>
                                <p>Analysis: <span>"{selectedRPConversation.analysis}"</span></p>
                                <button
                                    className='btn btn--sm btn--radius-sm btn--primary'
                                    onClick={handleViewTranscript}
                                    disabled={!!transcript}
                                >
                                    View Transcript
                                </button>
                            </div>
                        </div>
                        {!!rapport_collapses.length && (
                            <div className='mbts_analysis__report__behavioral_audit__content__right'>
                                <p className='mbts_analysis__report__behavioral_audit__content__title'>Response Analysis</p>
                                <p className='mbts_analysis__report__behavioral_audit__content__descr'>Learn what you could've done differently.</p>
                                <div className='mbts_analysis__report__behavioral_audit__content__rapport_collapse'>
                                    <div className='mbts_analysis__report__behavioral_audit__content__rapport_collapse__head'>
                                        <p>Observed Responses</p>
                                        {rapport_collapses.length > 1 && (
                                            <div className='mbts_analysis__report__behavioral_audit__content__rapport_collapse__head__nav'>
                                                <p>{selectedCollapse.idx + 1}/{rapport_collapses.length}</p>
                                                <div className='mbts_analysis__report__behavioral_audit__content__rapport_collapse__head__nav__buttons'>
                                                    <button
                                                        disabled={selectedCollapse.idx === 0}
                                                        onClick={prevCollapse}
                                                    >
                                                        <ChevronLeft />
                                                    </button>
                                                    <button
                                                        disabled={selectedCollapse.idx >= (rapport_collapses.length - 1)}
                                                        onClick={nextCollapse}
                                                    >
                                                        <ChevronRight />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className='mbts_analysis__report__behavioral_audit__content__rapport_collapse__list'>
                                        <div className='mbts_analysis__report__behavioral_audit__content__rapport_collapse__list__item--opening'>
                                            <p>{getElapsedLabel(selectedRPConversation.session_start, selectedCollapse.collapse.chat.created_at)} - {selectedCollapse.collapse.context}</p>
                                        </div>
                                        <div className='mbts_analysis__report__behavioral_audit__content__rapport_collapse__list__item--response'>
                                            <p>Your Response</p>
                                            <p>"{selectedCollapse.collapse.chat.message}"</p>
                                        </div>
                                        <div className='mbts_analysis__report__behavioral_audit__content__rapport_collapse__list__item--impact'>
                                            <p>Impact Analysis</p>
                                            <p>{selectedCollapse.collapse.impact_analysis}</p>
                                        </div>
                                        <div className='mbts_analysis__report__behavioral_audit__content__rapport_collapse__list__item--recommended'>
                                            <p>Recommended Alternative</p>
                                            <p>"{selectedCollapse.collapse.recommended_alternative}"</p>
                                            <p>Strategic Shift</p>
                                            <p>{selectedCollapse.collapse.strategic_shift}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {!!transcript && !!selectedCollapse && !!selectedRPConversation && (
                    <div className='mbts_analysis__report__behavioral_audit__conversation__wrapper'>
                        <div className='mbts_analysis__report__behavioral_audit__conversation'>
                            <div className='mbts_analysis__report__behavioral_audit__conversation__head'>
                                <div className='mbts_analysis__report__behavioral_audit__conversation__head__left'>
                                    <MessageSquare />
                                    <div>
                                        <p>Meeting Transcript</p>
                                        <p>{transcript.persona.title} ({transcript.persona.person})</p>
                                    </div>
                                </div>
                                <div className='mbts_analysis__report__behavioral_audit__conversation__head__right'>
                                    <button onClick={() => setTranscript(null)}><X /></button>
                                </div>
                            </div>
                            <div className='mbts_analysis__report__behavioral_audit__conversation__chat'>
                                {transcript.chats.map(chat => {
                                    const baseClass = 'mbts_analysis__report__behavioral_audit__conversation__chat__msg';
                                    const isCritical = chat.id === selectedCollapse.collapse.chat_id;
                                    let userIdentity = `${current_role} (You)`;
                                    if (chat.source === EChatSource.PERSONA) {
                                        userIdentity = `${transcript.persona.title} (${transcript.persona.person})`;
                                    };

                                    return (
                                        <div
                                            key={chat.id}
                                            id={`chat_msg--${chat.id}`}
                                            className={`${baseClass} ${baseClass}--${chat.source} ${isCritical ? `${baseClass}--critical_turn` : ''}`}
                                        >
                                            <p className={`${baseClass}__user_and_time`}>
                                                {getElapsedLabel(selectedRPConversation.session_start, chat.created_at)} <span>{userIdentity}</span>
                                            </p>
                                            <p className={`${baseClass}__text`}>
                                                {chat.message}
                                            </p>
                                            {isCritical &&
                                                <div className={`${baseClass}__critical_turn`}>
                                                    <div />
                                                    <p>CRITICAL TURN: {selectedCollapse.collapse.critical_turn}</p>
                                                </div>
                                            }
                                        </div>
                                    );
                                })}
                            </div>
                            <div className='mbts_analysis__report__behavioral_audit__conversation__bottom'>
                                <div className='mbts_analysis__report__behavioral_audit__conversation__bottom__legend'>
                                    <div />
                                    <p>{getElapsedLabel(selectedRPConversation.session_start, selectedCollapse.collapse.chat.created_at)} - Rapport Collapse</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};